import { useMemo, useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { BotaoAdicionar } from "./BotaoAdicionar";
import { AdicionarDaBaseDialog } from "@/components/catalogo/base/AdicionarDaBaseDialog";
import { CatalogItemRow } from "./CatalogItemRow";
import { CatalogoBusca, combina } from "./CatalogoBusca";
import { ServiceCategoryDialog, type ServiceCategoryEditTarget } from "./ServiceCategoryDialog";
import { BotaoFichas, ListaFichas, type FichaResumo } from "./ServicoFichas";
import { ServicosPorCategoria } from "./ServicosPorCategoria";
import { useLigarServicos } from "./use-ligar-servicos";

type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | {
      mode: "edit";
      target: ServiceCategoryEditTarget;
      aba?: "dados" | "ficha";
      /** Qual ficha abrir — quem clicou numa da lista quer aquela, não a primeira do serviço. */
      ficha?: { id: string; materialOptionId: string | null };
    };

const cardCls = "rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card";
const sectionTitleCls = "text-sm font-semibold text-zinc-900 dark:text-foreground";

export function ServiceCategorySection() {
  const utils = trpc.useUtils();
  const listQuery = trpc.prosthesis.catalog.myServiceCategories.useQuery();
  const fichasQuery = trpc.fichaTecnica.resumo.useQuery();
  const [dialogState, setDialogState] = useState<DialogState>({ mode: "closed" });
  const [busca, setBusca] = useState("");
  const [daBase, setDaBase] = useState(false);
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const { ligarServico, ligarCategoria, ocupado } = useLigarServicos();

  const remove = trpc.prosthesis.catalog.deleteCustomServiceCategory.useMutation({
    onSuccess: () => {
      toast.success("Serviço excluído.");
      utils.prosthesis.catalog.myServiceCategories.invalidate();
      utils.prosthesis.catalog.serviceCategories.invalidate();
      utils.fichaTecnica.resumo.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir serviço."),
  });

  // Uma consulta só traz as fichas de todos os serviços; aqui elas voltam a ficar por serviço.
  const fichasPorServico = useMemo(() => {
    const mapa = new Map<string, FichaResumo[]>();
    for (const f of (fichasQuery.data ?? []) as FichaResumo[]) {
      if (!mapa.has(f.serviceCategoryId)) mapa.set(f.serviceCategoryId, []);
      mapa.get(f.serviceCategoryId)!.push(f);
    }
    return mapa;
  }, [fichasQuery.data]);

  // A busca alcança o nome do serviço e o nome das fichas dele: procurar "BioCrown" tem que achar
  // o serviço que tem essa receita, mesmo o nome dele não dizendo nada de resina.
  const todos = useMemo(() => (listQuery.data ?? []) as any[], [listQuery.data]);
  const filtrados = useMemo(() => {
    if (!busca.trim()) return todos;
    return todos.filter(
      (s) => combina(s.label, busca) || (fichasPorServico.get(s.id) ?? []).some((f) => combina(f.name, busca)),
    );
  }, [todos, busca, fichasPorServico]);
  const nenhumAtivo = listQuery.isSuccess && todos.length > 0 && !todos.some((s) => s.active);

  const alternar = (id: string) =>
    setAbertos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div id="servicos" className={cardCls}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 data-tour="catalogo-gerenciar-secao-titulo" className={sectionTitleCls}>Serviços</h2>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
            O catálogo do seu laboratório — só os ativos aparecem pros dentistas ao montar um pedido.
          </p>
        </div>
        <BotaoAdicionar topo onClick={() => setDaBase(true)}>
          Adicionar serviço
        </BotaoAdicionar>
        <AdicionarDaBaseDialog tipo="servicos" open={daBase} onClose={() => setDaBase(false)} onCriarNovo={() => setDialogState({ mode: "create" })} />
      </div>

      {nenhumAtivo && (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          Nenhum serviço ativo ainda. Marque abaixo as categorias e os serviços que o laboratório faz — só eles
          aparecem pros dentistas.
        </p>
      )}

      <CatalogoBusca
        valor={busca}
        onChange={setBusca}
        placeholder="Buscar serviço ou ficha técnica"
        achados={filtrados.length}
      />

      <div className="mt-3">
        <ServicosPorCategoria
          servicos={filtrados}
          buscando={!!busca.trim()}
          ocupado={ocupado || remove.isPending}
          onToggleCategoria={ligarCategoria}
          umPorLinha
          renderItem={(s) => {
            const fichas = fichasPorServico.get(s.id) ?? [];
            const aberto = abertos.has(s.id);
            return (
              <div key={s.id}>
                <CatalogItemRow
                  label={s.label}
                  proprio={s.originGlobalId === null && !s.origemNaBaseId}
                  enabled={s.active}
                  disabled={ocupado || remove.isPending}
                  onToggle={(enabled) => ligarServico(s.id, enabled)}
                  onEdit={() => setDialogState({ mode: "edit", target: s })}
                  onDelete={() => {
                    if (window.confirm(`Excluir o serviço "${s.label}"?`)) remove.mutate({ serviceCategoryId: s.id });
                  }}
                  extra={<BotaoFichas quantas={fichas.length} aberto={aberto} onAlternar={() => alternar(s.id)} label={s.label} />}
                />
                {aberto && (
                  <ListaFichas
                    fichas={fichas}
                    onAbrir={(f) =>
                      setDialogState({
                        mode: "edit",
                        target: s,
                        aba: "ficha",
                        ficha: { id: f.id, materialOptionId: f.materialOptionId },
                      })
                    }
                  />
                )}
              </div>
            );
          }}
        />
      </div>

      <ServiceCategoryDialog
        open={dialogState.mode !== "closed"}
        editTarget={dialogState.mode === "edit" ? dialogState.target : null}
        abaInicial={dialogState.mode === "edit" ? dialogState.aba : undefined}
        fichaInicial={dialogState.mode === "edit" ? dialogState.ficha : undefined}
        onClose={() => setDialogState({ mode: "closed" })}
      />
    </div>
  );
}
